import { Divider, useMediaQuery, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import { ComponentPropsWithoutRef, ElementType } from 'react';
import { paymentBreakpoints } from 'src/pages/payment-msi/PaymentMsiV2/styles';
import { PaymentPlanOption } from 'src/pages/payment-msi/PaymentMsiV2/utils';
import { AppTheme } from 'src/themes/muiTheme';

interface PaymentOptionChipProps {
  isActive: boolean;
}
const PaymentOptionChip = styled(Chip, {
  shouldForwardProp: (prop) => prop !== 'isActive',
})<PaymentOptionChipProps>(({ theme, isActive }) => {
  return {
    backgroundColor: isActive
      ? theme.palette.primary.contrastText
      : theme.palette.primary.main,
    color: isActive
      ? theme.palette.text.primary
      : theme.palette.primary.contrastText,
    paddingLeft: '15px',
    paddingRight: '15px',
    paddingTop: '4px',
    paddingBottom: '4px',
    '& .MuiChip-label': {
      padding: 0,
    },
  };
});

const PaymentOptionContentWrapper = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  rowGap: '8px',
  position: 'relative',
});

type PaymentOptionTextProps = ComponentPropsWithoutRef<typeof Typography> & {
  isActive: boolean;
  component?: ElementType;
};
const PaymentOptionText = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'isActive',
})<PaymentOptionTextProps>(({ theme, isActive }) => {
  return {
    color: isActive
      ? theme.palette.primary.contrastText
      : theme.palette.text.primary,
  };
});

interface SharedPaymentPlanLabelProps {
  isActive: boolean;
  option: PaymentPlanOption;
}

const sharedPaymentOptionLabelStyles = {
  paymentOptionLabel: (theme: AppTheme) => {
    const mobile = theme.breakpoints.down('sm');

    return {
      display: 'flex',
      justifyContent: 'space-between',
      flexGrow: 1,
      [mobile]: {
        flexDirection: 'column',
        alignItems: 'flex-start',
      },
    };
  },
  paymentOptionPaymentPrice: (theme: AppTheme) => {
    const mobile = theme.breakpoints.down('sm');

    return {
      display: 'flex',
      alignItems: 'baseline',
      [mobile]: {
        columnGap: '8px',
      },
    };
  },
};

const desktopPaymentPlanLabelStyles = {
  paymentOptionPaymentInfo: {
    alignItems: 'flex-end',
  },
  paymentOptionPeriodText: (theme: AppTheme) => {
    const tabletMaxSize = theme.breakpoints.values.lg + 1;
    const tablet = theme.breakpoints.down(tabletMaxSize);

    return {
      [tablet]: {
        fontSize: '32px',
        lineHeight: '46px',
      },
    };
  },
  paymentOptionPriceText: (theme: AppTheme) => {
    const tabletMaxSize = theme.breakpoints.values.lg + 1;
    const tablet = theme.breakpoints.down(tabletMaxSize);

    return {
      [tablet]: {
        fontSize: '32px',
        lineHeight: '46px',
      },
    };
  },
};
function DesktopPaymentPlanLabel({
  isActive,
  option,
}: SharedPaymentPlanLabelProps) {
  return (
    <Box sx={sharedPaymentOptionLabelStyles.paymentOptionLabel}>
      <PaymentOptionContentWrapper>
        <Box display={'flex'} columnGap={'24px'} alignItems={'center'}>
          <PaymentOptionText
            sx={desktopPaymentPlanLabelStyles.paymentOptionPeriodText}
            isActive={isActive}
            component={'h5'}
            variant={'h4'}
          >
            {option.title}
          </PaymentOptionText>
          {option.value === 'ANNUAL' ? (
            <PaymentOptionChip
              isActive={isActive}
              label={`Save ${option.saveAmount}`}
            />
          ) : null}
        </Box>
        <PaymentOptionText
          isActive={isActive}
          component={'p'}
          variant={'body1'}
        >
          {option.description}
        </PaymentOptionText>
      </PaymentOptionContentWrapper>
      <PaymentOptionContentWrapper
        sx={desktopPaymentPlanLabelStyles.paymentOptionPaymentInfo}
      >
        <Box
          component={'div'}
          sx={sharedPaymentOptionLabelStyles.paymentOptionPaymentPrice}
        >
          <PaymentOptionText
            sx={desktopPaymentPlanLabelStyles.paymentOptionPriceText}
            isActive={isActive}
            component={'p'}
            variant={'h4'}
          >
            {option.price}
          </PaymentOptionText>
          <PaymentOptionText
            isActive={isActive}
            component={'p'}
            variant={'body1'}
          >
            {option.periodText}
          </PaymentOptionText>
        </Box>
        <PaymentOptionText isActive={isActive} variant={'body1'}>
          Due today: {option.duePayment}
        </PaymentOptionText>
      </PaymentOptionContentWrapper>
    </Box>
  );
}

const mobilePaymentPlanLabelStyles = {
  labelTitleWrapper: (theme: AppTheme) => {
    const smallMobileMaxSize = paymentBreakpoints.smallMobile;
    const smallMobile = theme.breakpoints.down(smallMobileMaxSize);

    return {
      display: 'flex',
      justifyContent: 'space-between',
      width: '100%',
      [smallMobile]: {
        flexDirection: 'column-reverse',
        width: 'fit-content',
      },
    };
  },
  paymenetPrice: (theme: AppTheme) => {
    const smallMobileMaxSize = paymentBreakpoints.smallMobile;
    const smallMobile = theme.breakpoints.down(smallMobileMaxSize);

    return {
      ...sharedPaymentOptionLabelStyles.paymentOptionPaymentPrice(theme),
      [smallMobile]: {
        flexDirection: 'column',
      },
    };
  },
  paymentPriceTitle: (theme: AppTheme) => {
    const mobile = theme.breakpoints.down('sm');

    return {
      [mobile]: {
        fontSize: '36px',
        lineHeight: '100%',
        letterSpacing: '-0.72px',
      },
    };
  },
};
function MobilePaymentPlanLabel({
  isActive,
  option,
}: SharedPaymentPlanLabelProps) {
  const theme = useTheme<AppTheme>();

  return (
    <Box sx={sharedPaymentOptionLabelStyles.paymentOptionLabel}>
      <Box marginBottom={'16px'} width={'100%'}>
        <Box sx={mobilePaymentPlanLabelStyles.labelTitleWrapper}>
          <PaymentOptionText
            isActive={isActive}
            component={'h5'}
            variant={'h4'}
          >
            {option.title}
          </PaymentOptionText>
          {option.value === 'ANNUAL' ? (
            <PaymentOptionChip
              isActive={isActive}
              label={`Save ${option.saveAmount}`}
            />
          ) : null}
        </Box>
        <Box component={'div'} sx={mobilePaymentPlanLabelStyles.paymenetPrice}>
          <PaymentOptionText
            isActive={isActive}
            sx={mobilePaymentPlanLabelStyles.paymentPriceTitle}
            component={'p'}
            variant={'h4'}
          >
            {option.price}
          </PaymentOptionText>
          <PaymentOptionText
            isActive={isActive}
            component={'p'}
            variant={'body1'}
          >
            {option.periodText}
          </PaymentOptionText>
        </Box>
      </Box>
      <Divider
        sx={{
          width: '100%',
          borderColor: theme.palette.text.secondary,
          marginBottom: '4px',
        }}
      />
      <Box display={'flex'} flexDirection={'column'} rowGap={'4px'}>
        <PaymentOptionText
          isActive={isActive}
          component={'p'}
          variant={'body1'}
          fontSize={'12px'}
        >
          {option.description}
        </PaymentOptionText>
        <PaymentOptionText
          fontSize={'12px'}
          isActive={isActive}
          variant={'body1'}
        >
          Due today: {option.duePayment}
        </PaymentOptionText>
      </Box>
    </Box>
  );
}

export function PaymentPlanFieldOptionLabel(
  props: SharedPaymentPlanLabelProps,
) {
  const isMobile = useMediaQuery<AppTheme>((theme) => {
    const mobileMaxSize = theme.breakpoints.values.sm + 1;
    return theme.breakpoints.down(mobileMaxSize);
  });

  return isMobile ? (
    <MobilePaymentPlanLabel {...props} />
  ) : (
    <DesktopPaymentPlanLabel {...props} />
  );
}
